import { memo, useCallback } from "react";
import classNames from "classnames";
import { format, parseISO } from "date-fns";
import { FinancialAccount } from "@common/types/financialAccount";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { EntitySettingsPages } from "@components/settings/constants";
import { getEntityPaneKey } from "@components/settings/lib";
import { useSettings } from "@components/settings/SettingsProvider";
import AccountStatementsAvailableIcon from "@icons/statementStatuses/StatementAvailable.svg?react";
import AccountStatementsUnavailableIcon from "@icons/statementStatuses/StatementUnavailable.svg?react";
import AccountStatementsSyncingIcon from "@icons/statementStatuses/StatementSyncing.svg?react";
import ConnectedIcon from "@icons/accountStatuses/Connected.svg?react";
import NeedsAttentionIcon from "@icons/accountStatuses/NeedsAttention.svg?react";
import DisabledIcon from "@icons/accountStatuses/Disabled.svg?react";
import { useFinancialAccountSyncTimes } from "@hooks/useFinancialAccountSyncTimes";
import { usePlaidStatementsQualified } from "@hooks/usePlaidStatementsQualified";
import { timeAgo } from "@helpers/date";
import ImportedIcon from "@icons/accountStatuses/Imported.svg?react";
import { HStack } from "@design-system/HStack/HStack";
import {
    financialAccountHasIncompleteStripe,
    financialAccountIsImportOnly,
} from "@helpers/financialAccount";
import { hasFailedConnection } from "../accountsView.helpers";
import {
    StatementSyncState,
    getAccountStatementSyncState,
} from "../accountStatementSync.helpers";
import styles from "../accountsView.module.scss";
import {
    AccountStatusType,
    getAccountStatusTooltip,
} from "./accountStatusTooltip";

interface Props {
    account: FinancialAccount;
}

const STATUS_CONFIG: Record<
    AccountStatusType,
    {
        icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
        className?: string;
        showStatementsIndicator: boolean;
    }
> = {
    [AccountStatusType.DISABLED]: {
        icon: DisabledIcon,
        className: styles.statusDisabled,
        showStatementsIndicator: false,
    },
    [AccountStatusType.NEEDS_ATTENTION]: {
        icon: NeedsAttentionIcon,
        className: styles.statusError,
        showStatementsIndicator: false,
    },
    [AccountStatusType.INCOMPLETE]: {
        icon: NeedsAttentionIcon,
        className: styles.statusError,
        showStatementsIndicator: false,
    },
    [AccountStatusType.IMPORTED]: {
        icon: ImportedIcon,
        showStatementsIndicator: true,
    },
    [AccountStatusType.CONNECTED]: {
        icon: ConnectedIcon,
        showStatementsIndicator: true,
    },
};

const STATEMENT_ICON_MAP: Record<
    StatementSyncState,
    React.ComponentType<React.SVGProps<SVGSVGElement>> | null
> = {
    "not-qualified": null,
    "not-supported": null,
    "supported-not-enabled": AccountStatementsUnavailableIcon,
    "enabled-not-synced": AccountStatementsSyncingIcon,
    "synced-none-found": AccountStatementsUnavailableIcon,
    "synced-found": AccountStatementsAvailableIcon,
};

const STATEMENT_SYNC_TOOLTIPS: Record<StatementSyncState, string> = {
    "not-qualified": "Statements not included in plan",
    "not-supported": "Statements not supported",
    "supported-not-enabled": "Enable statement sync",
    "enabled-not-synced": "Syncing",
    "synced-none-found": "Syncing, no statements found",
    "synced-found": "Statements automatically synced",
};

const StatementsTooltipContent: React.FC<{
    readonly state: StatementSyncState;
    readonly lastStatementDate: string | null;
}> = ({ state, lastStatementDate }) => {
    if (state !== "synced-found" || !lastStatementDate) {
        return <>{STATEMENT_SYNC_TOOLTIPS[state]}</>;
    }

    return (
        <>
            Last statement:{" "}
            {format(parseISO(lastStatementDate), fullDateFullYearFormat)}
        </>
    );
};

export const AccountStatusCell = memo(({ account }: Props) => {
    const syncTimes = useFinancialAccountSyncTimes(account);
    const { open } = useSettings();
    const isPlaidStatementsQualified = usePlaidStatementsQualified();

    const { state: statementState, lastStatementDate } =
        getAccountStatementSyncState({
            account,
            isPlaidStatementsQualified,
        });

    const openAccountSettings = useCallback(() => {
        if (!account.entityId) {
            return;
        }
        open([
            getEntityPaneKey(EntitySettingsPages.SETTINGS, account.entityId),
            account.id.toString(),
        ]);
    }, [account.entityId, account.id, open]);

    let status = AccountStatusType.CONNECTED;
    if (account.disabled) {
        status = AccountStatusType.DISABLED;
    } else if (hasFailedConnection(account)) {
        status = AccountStatusType.NEEDS_ATTENTION;
    } else if (financialAccountHasIncompleteStripe(account)) {
        status = AccountStatusType.INCOMPLETE;
    } else if (financialAccountIsImportOnly(account)) {
        status = AccountStatusType.IMPORTED;
    }

    const {
        icon: StatusIcon,
        className,
        showStatementsIndicator,
    } = STATUS_CONFIG[status];
    const statusTooltip = getAccountStatusTooltip({ status, syncTimes });

    const StatementsIcon = statementState
        ? STATEMENT_ICON_MAP[statementState]
        : null;

    const isClickable = statementState === "supported-not-enabled";

    return (
        <ViewTableCell className={classNames(styles.statusCell, className)}>
            <HStack gap={8} align="center">
                <Tooltip
                    title={statusTooltip.title}
                    content={statusTooltip.content}
                    bullets={statusTooltip.bullets}
                >
                    <HStack as="span" gap={8} align="center">
                        <span className={styles.statusConnected}>
                            <StatusIcon
                                className={styles.statusIcon}
                                width={14}
                                height={14}
                            />
                        </span>
                        <span
                            className={classNames(styles.lastSyncInline, {
                                [styles.disabled]: account.disabled,
                            })}
                        >
                            {syncTimes.lastSyncedAt &&
                                timeAgo(syncTimes.lastSyncedAt, {
                                    hideAgo: true,
                                })}
                        </span>
                    </HStack>
                </Tooltip>
                {showStatementsIndicator &&
                    statementState &&
                    StatementsIcon && (
                        <Tooltip
                            content={
                                <StatementsTooltipContent
                                    state={statementState}
                                    lastStatementDate={lastStatementDate}
                                />
                            }
                        >
                            <span
                                className={classNames(styles.statementsIcon, {
                                    [styles.statementsIconClickable]:
                                        isClickable,
                                })}
                                role={isClickable ? "button" : undefined}
                                tabIndex={isClickable ? 0 : undefined}
                                onClick={
                                    isClickable
                                        ? openAccountSettings
                                        : undefined
                                }
                                onKeyDown={
                                    isClickable
                                        ? (e) => {
                                              if (
                                                  e.key === "Enter" ||
                                                  e.key === " "
                                              ) {
                                                  e.preventDefault();
                                                  openAccountSettings();
                                              }
                                          }
                                        : undefined
                                }
                            >
                                <StatementsIcon
                                    className="icon-color-current"
                                    width={12}
                                    height={12}
                                />
                            </span>
                        </Tooltip>
                    )}
            </HStack>
        </ViewTableCell>
    );
});

AccountStatusCell.displayName = "AccountStatusCell";
